import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  ServiceInterval,
  ServiceIntervalList,
  type CreateIntervalBody,
  type UpdateIntervalBody,
} from '@klunkers/shared'
import { api } from '@/api/http'

/** Service intervals with live status (computed on the server from derived usage). */
export const useIntervalsStore = defineStore('intervals', () => {
  const lists = ref<Record<string, ServiceInterval[]>>({})

  async function load(key: 'all' | 'due' | `bike:${string}` | `part:${string}`) {
    const query =
      key === 'all'
        ? 'status=all'
        : key === 'due'
          ? 'status=due'
          : key.startsWith('bike:')
            ? `bike_id=${key.slice(5)}`
            : `component_id=${key.slice(5)}`
    const { intervals } = await api(`/intervals?${query}`, { schema: ServiceIntervalList })
    lists.value = { ...lists.value, [key]: intervals }
    return intervals
  }

  /** Any change can move an interval between lists, so refetch what is on screen. */
  async function refresh() {
    await Promise.all(
      Object.keys(lists.value).map((k) => load(k as Parameters<typeof load>[0]).catch(() => {})),
    )
  }

  async function create(body: CreateIntervalBody) {
    const i = await api('/intervals', { method: 'POST', body, schema: ServiceInterval })
    await refresh()
    return i
  }
  async function update(id: string, body: UpdateIntervalBody) {
    await api(`/intervals/${id}`, { method: 'PATCH', body, schema: ServiceInterval })
    await refresh()
  }
  async function reset(id: string, at?: string) {
    await api(`/intervals/${id}/reset`, {
      method: 'POST',
      body: at ? { at } : {},
      schema: ServiceInterval,
    })
    await refresh()
  }
  async function remove(id: string) {
    await api(`/intervals/${id}`, { method: 'DELETE' })
    await refresh()
  }

  return { lists, load, refresh, create, update, reset, remove }
})
